/**
 * Integrytas Group - Unified Audit Trail (UAT)
 * Page: UAT.TIMELINE
 *
 * Diagnostic reconstruction interface: query a record's complete historical
 * timeline (cell-level before/after deltas in chronological order) and replay
 * them forward to reconstruct the entity's exact state at any point in time.
 */

import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';

interface Delta {
  audit_log_id: number; action: string; at: string;
  before: Record<string, unknown> | null; after: Record<string, unknown> | null;
  user_id: number | null; workspace_id: number | null; api_route: string | null; txid: number | null; session: string | null;
}
interface Reconstruct { entity: string; record_id: string; as_of: string; deltas_applied: number; deleted: boolean; state: Record<string, unknown> | null }

const TIMELINE_GRID_COLS: GridColumnDef[] = [
  { key: 'at',           header: 'When',      defaultWidth: 200 },
  { key: 'action',       header: 'Action',    defaultWidth: 120 },
  { key: 'actor',        header: 'By',        defaultWidth: 160 },
  { key: 'workspace',    header: 'Workspace', defaultWidth: 120 },
  { key: 'api_route',    header: 'Route',     defaultWidth: 200 },
  { key: 'txid',         header: 'Txid',      defaultWidth: 120 },
];

export interface UatTimelinePageProps { session: AuthSession }

export function UatTimelinePage({ session }: UatTimelinePageProps) {
  return (
    <PersonalizationProvider pageCode="UAT.TIMELINE" session={session}>
      <UatTimelinePageInner session={session} />
    </PersonalizationProvider>
  );
}

function UatTimelinePageInner({ session }: UatTimelinePageProps) {
  const { t } = useTranslation();
  const [entity, setEntity] = useState('');
  const [record, setRecord] = useState('');
  const [deltas, setDeltas] = useState<Delta[]>([]);
  const [at, setAt] = useState('');
  const [recon, setRecon] = useState<Reconstruct | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [workspaceNames, setWorkspaceNames] = useState<Record<number, string>>({});
  // §17.B13: expand/contract related-children. A timeline delta is a leaf audit
  // event with no loaded child collection, so the control renders inert.
  const detail = useExpandedRows<number>();
  // TODO: wire relations — a delta references no separately-loaded children.
  const buildRelatedGroups = (): RelatedChildGroup[] => [];

  const loadTimeline = async () => {
    if (!entity.trim() || !record.trim()) return;
    setBusy(true); setError(null); setRecon(null);
    try {
      const { data, error: e } = await apiDb.rpc('uat_timeline', { p_entity: entity.trim(), p_record: record.trim() });
      if (e) throw e;
      const list = (data ?? []) as Delta[];
      setDeltas(list);
      const ids = Array.from(new Set(list.map(d => d.user_id).filter((x): x is number => x != null)));
      if (ids.length) {
        const { data: us } = await apiDb.from('csm_user_master').select('user_id, display_name, email').in('user_id', ids);
        const m: Record<number, string> = {};
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) m[u.user_id] = u.display_name || u.email || '—';
        setUserNames(m);
      } else { setUserNames({}); }
      const wsIds = Array.from(new Set(list.map(d => d.workspace_id).filter((x): x is number => x != null)));
      if (wsIds.length) {
        const { data: ws } = await apiDb.from('une_workspace').select('une_workspace_id, name').in('une_workspace_id', wsIds);
        const wm: Record<number, string> = {};
        for (const w of (ws ?? []) as { une_workspace_id: number; name: string | null }[]) wm[w.une_workspace_id] = w.name || '—';
        setWorkspaceNames(wm);
      } else { setWorkspaceNames({}); }
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const reconstruct = async () => {
    if (!entity.trim() || !record.trim()) return;
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('uat_reconstruct', { p_entity: entity.trim(), p_record: record.trim(), p_at: at ? new Date(at).toISOString() : new Date().toISOString() });
      if (e) throw e;
      setRecon(data as Reconstruct);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const changedKeys = (d: Delta) => Array.from(new Set([...Object.keys(d.before ?? {}), ...Object.keys(d.after ?? {})]));

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('uat.title', 'Audit Timeline')}</span>
        <input value={entity} onChange={e => setEntity(e.target.value)} placeholder={t('uat.placeholder.entity', 'entity (table)')} className="h-7 w-44 rounded border border-csm-border px-2 font-mono text-csm-xs" />
        <input value={record} onChange={e => setRecord(e.target.value)} placeholder={t('uat.placeholder.record', 'record id')} className="h-7 w-28 rounded border border-csm-border px-2 font-mono text-csm-xs" />
        <button onClick={() => void loadTimeline()} className="h-7 rounded border border-sidebar bg-sidebar px-2 text-csm-sm text-white hover:bg-sidebar-alt">{t('uat.load_timeline', 'Load Timeline')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('uat.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('uat.load_failed', "Couldn't load the timeline.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void loadTimeline()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <div className="mb-4 flex flex-wrap items-end gap-2 rounded border border-csm-border bg-white p-2">
          <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('uat.reconstruct_as_of', 'Reconstruct state as of')}</span>
          <input type="datetime-local" value={at} onChange={e => setAt(e.target.value)} className="h-8 rounded border border-csm-border px-2 text-csm-sm" />
          <button onClick={() => void reconstruct()} className="h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover">{t('uat.reconstruct', 'Reconstruct')}</button>
          {recon && <span className="text-csm-xs text-csm-watermark">{t('uat.deltas_applied', '{{n}} delta(s) applied', { n: recon.deltas_applied })}{recon.deleted ? t('uat.deleted_suffix', ' · record was deleted') : ''}</span>}
        </div>
        {recon && (
          <div className="mb-4 rounded border border-primary/40 bg-primary/5 p-2">
            <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-primary">{t('uat.reconstructed_state_at', 'Reconstructed State @ {{ts}}', { ts: new Date(recon.as_of).toLocaleString() })}</div>
            <pre className="max-h-72 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{recon.deleted ? t('uat.deleted_state', '(record did not exist / was deleted at this time)') : JSON.stringify(recon.state, null, 2)}</pre>
          </div>
        )}

        <PersonalizedGrid componentKey="grid" columns={TIMELINE_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('uat.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-right">Δ</th>
                </tr>
              </thead>
              <tbody>
                {deltas.map(d => (
                  <Fragment key={d.audit_log_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton open={detail.isOpen(d.audit_log_id)} hasChildren={false} onToggle={() => detail.toggle(d.audit_log_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'at'           && <span className="text-csm-2xs text-csm-watermark">{new Date(d.at).toLocaleString()}.{String(new Date(d.at).getMilliseconds()).padStart(3, '0')}</span>}
                          {col.key === 'action'       && <span className="text-csm-xs">{d.action}</span>}
                          {col.key === 'actor'        && <span className="text-csm-xs">{d.user_id == null ? '—' : (userNames[d.user_id] ?? '—')}</span>}
                          {col.key === 'workspace'    && <span className="text-csm-2xs">{d.workspace_id == null ? '—' : (workspaceNames[d.workspace_id] ?? '—')}</span>}
                          {col.key === 'api_route'    && <span className="font-mono text-csm-2xs">{d.api_route ?? '—'}</span>}
                          {col.key === 'txid'         && <span className="font-mono text-csm-2xs">{d.txid ?? '—'}</span>}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-2 py-1 text-right"><button onClick={() => setOpen(open === d.audit_log_id ? null : d.audit_log_id)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-2xs hover:bg-csm-surface-alt">{open === d.audit_log_id ? t('uat.hide', 'Hide') : t('uat.diff', 'Diff')}</button></td>
                    </tr>
                    {detail.isOpen(d.audit_log_id) && (
                      <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups()} session={session} /></td></tr>
                    )}
                    {open === d.audit_log_id && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt px-2 py-1">
                          <table className="min-w-full border-collapse">
                            <thead><tr className="text-csm-2xs uppercase text-csm-ink-soft"><th className="px-2 text-left">{t('uat.diffcol.field', 'Field')}</th><th className="px-2 text-left">{t('uat.diffcol.before', 'Before')}</th><th className="px-2 text-left">{t('uat.diffcol.after', 'After')}</th></tr></thead>
                            <tbody>
                              {changedKeys(d).map(k => (
                                <tr key={k}>
                                  <td className="px-2 py-0.5 font-mono text-csm-2xs">{k}</td>
                                  <td className="px-2 py-0.5 font-mono text-csm-2xs text-csm-danger">{JSON.stringify(d.before?.[k])}</td>
                                  <td className="px-2 py-0.5 font-mono text-csm-2xs text-primary">{JSON.stringify(d.after?.[k])}</td>
                                </tr>
                              ))}
                              {changedKeys(d).length === 0 && <tr><td colSpan={3} className="px-2 py-0.5 italic text-csm-watermark text-csm-2xs">{t('uat.no_delta', 'No field-level delta.')}</td></tr>}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {deltas.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3">
                    <EmptyState variant="no-data" title={t('uat.empty', 'Enter an entity + record id and load the timeline.')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>
    </div>
  );
}
